import type {
  AdminUserDetail,
  AdminUserListResponse,
  AdminUserRow,
  AuthResponse,
  BodyWeightLog,
  BodyWeightSummary,
  CalendarResponse,
  CatalogExercise,
  CompletedWorkout,
  ExerciseUnit,
  ExerciseHistoryItem,
  MonthStats,
  PlannedWorkout,
  PlatformStats,
  Profile,
  WeeklyGoalProgress,
  WorkoutTemplate,
} from "./types";
import { clearCache, invalidate } from "../lib/cache";
import type { ExerciseType } from "../lib/activity";
import type { ProgressRange } from "../lib/progress";
import type { RecordKind } from "../lib/records";
import {
  readCalendar,
  readHistoryItem,
  readCatalogExercise,
  readCompleted,
  readPlanned,
  readMonthStats,
  readProfile,
  readProgress,
  readRecords,
  readTemplate,
  progressPath,
  recordsPath,
  withWireExercises,
  writeWeeklyGoal,
} from "./adapters";

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ||
  "https://tgapi.fedya.party";

/** Failed request; `status` is the HTTP status (409 = conflict). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type TokenStore = {
  accessToken: string | null;
  refreshToken: string | null;
};

const tokens: TokenStore = {
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
};

export function setTokens(access: string, refresh: string) {
  tokens.accessToken = access;
  tokens.refreshToken = refresh;
  localStorage.setItem("accessToken", access);
  localStorage.setItem("refreshToken", refresh);
}

export function clearTokens() {
  tokens.accessToken = null;
  tokens.refreshToken = null;
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  // Cached data belongs to the session that is going away.
  clearCache();
}

export function getAccessToken() {
  return tokens.accessToken;
}

export function getApiBaseUrl() {
  return API_URL;
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!tokens.refreshToken) return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });
        if (!res.ok) {
          clearTokens();
          return false;
        }
        const data = (await res.json()) as AuthResponse;
        setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        clearTokens();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  auth = true,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  if (auth && tokens.accessToken) {
    headers.set("Authorization", `Bearer ${tokens.accessToken}`);
  }

  const url = `${API_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (err) {
    const hint = [
      `Failed to fetch ${url}`,
      `VITE_API_URL=${API_URL}`,
      "Check: API tunnel is up, URL is HTTPS (not localhost), backend running, /health opens in browser.",
    ].join(" | ");
    console.error(hint, err);
    throw new Error(hint);
  }

  if (res.status === 401 && auth) {
    const ok = await tryRefresh();
    if (ok && tokens.accessToken) {
      headers.set("Authorization", `Bearer ${tokens.accessToken}`);
      try {
        res = await fetch(url, { ...options, headers });
      } catch {
        throw new Error(`Failed to fetch ${url} (after refresh)`);
      }
    }
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      message = err?.error?.message || message;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Run a write; whatever its outcome, drop cached reads it may have changed. */
function writing<T>(p: Promise<T>, ...prefixes: string[]): Promise<T> {
  return p.finally(() => invalidate(...prefixes));
}

/**
 * Cache scopes touched by writes (see lib/cache.ts; keys are built in lib/data.ts).
 * Workouts, plans and templates also refresh the catalog: using an exercise
 * there is what locks its type (`hasHistory`).
 */
const PLANNED_SCOPE = ["calendar", "planned", "catalog"];
const COMPLETED_SCOPE = [...PLANNED_SCOPE, "completed", "stats", "profile", "records", "progress"];
const TEMPLATE_SCOPE = ["templates", "catalog"];
/** Catalog edits (name, icon, type) change how existing rows look. */
const CATALOG_EDIT_SCOPE = ["catalog", "calendar", "planned", "completed", "templates", "records", "progress"];

/** Catalog write body. The unit is no longer edited; the type is chosen once. */
interface CatalogWrite {
  muscleGroups?: string[];
  defaultUnit?: ExerciseUnit;
  exerciseType?: ExerciseType;
  icon?: string | null;
}

export const api = {
  loginWebApp(initData: string) {
    return request<AuthResponse>(
      "/api/auth/telegram/webapp",
      { method: "POST", body: JSON.stringify({ initData }) },
      false,
    );
  },

  loginWidget(payload: Record<string, string | number>) {
    return request<AuthResponse>(
      "/api/auth/telegram/widget",
      { method: "POST", body: JSON.stringify(payload) },
      false,
    );
  },

  logout() {
    return request("/api/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    }).finally(clearTokens);
  },

  getProfile() {
    return request<Profile>("/api/profile").then(readProfile);
  },

  setLanguage(language: string) {
    return writing(
      request<Profile>("/api/profile/language", {
        method: "PATCH",
        body: JSON.stringify({ language }),
      }),
      "profile",
    );
  },

  setReminderLead(reminderLeadMinutes: 15 | 30 | 60 | 120) {
    return writing(
      request<Profile>("/api/profile/reminder-lead", {
        method: "PATCH",
        body: JSON.stringify({ reminderLeadMinutes }),
      }),
      "profile",
    );
  },

  getCalendar(year: number, month: number) {
    return request<CalendarResponse>(
      `/api/workouts/calendar?year=${year}&month=${month}`,
    ).then(readCalendar);
  },

  listCompleted(from?: string, to?: string) {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const s = q.toString();
    return request<CompletedWorkout[]>(
      `/api/workouts/completed${s ? `?${s}` : ""}`,
    ).then((list) => list.map(readCompleted));
  },

  getCompleted(id: string) {
    return request<CompletedWorkout>(`/api/workouts/completed/${id}`).then(
      readCompleted,
    );
  },

  createCompleted(body: unknown) {
    return writing(
      request<CompletedWorkout>("/api/workouts/completed", {
        method: "POST",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readCompleted),
      ...COMPLETED_SCOPE,
    );
  },

  updateCompleted(id: string, body: unknown) {
    return writing(
      request<CompletedWorkout>(`/api/workouts/completed/${id}`, {
        method: "PUT",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readCompleted),
      ...COMPLETED_SCOPE,
    );
  },

  deleteCompleted(id: string) {
    return writing(
      request(`/api/workouts/completed/${id}`, { method: "DELETE" }),
      ...COMPLETED_SCOPE,
    );
  },

  listPlanned(from?: string, to?: string) {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const s = q.toString();
    return request<PlannedWorkout[]>(
      `/api/workouts/planned${s ? `?${s}` : ""}`,
    ).then((list) => list.map(readPlanned));
  },

  getPlanned(id: string) {
    return request<PlannedWorkout>(`/api/workouts/planned/${id}`).then(
      readPlanned,
    );
  },

  createPlanned(body: unknown) {
    return writing(
      request<PlannedWorkout>("/api/workouts/planned", {
        method: "POST",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readPlanned),
      ...PLANNED_SCOPE,
    );
  },

  updatePlanned(id: string, body: unknown) {
    return writing(
      request<PlannedWorkout>(`/api/workouts/planned/${id}`, {
        method: "PUT",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readPlanned),
      ...PLANNED_SCOPE,
    );
  },

  deletePlanned(id: string) {
    return writing(
      request(`/api/workouts/planned/${id}`, { method: "DELETE" }),
      ...PLANNED_SCOPE,
    );
  },

  completePlanned(id: string, body?: unknown) {
    return writing(
      request<{ planned: PlannedWorkout; completed: CompletedWorkout }>(
        `/api/workouts/planned/${id}/complete`,
        { method: "POST", body: JSON.stringify(withWireExercises(body ?? {})) },
      ).then((r) => ({
        planned: readPlanned(r.planned),
        completed: readCompleted(r.completed),
      })),
      ...COMPLETED_SCOPE,
    );
  },

  exerciseHistory(name: string, limit = 5) {
    return request<ExerciseHistoryItem[]>(
      `/api/workouts/exercises/history?name=${encodeURIComponent(
        name,
      )}&limit=${limit}`,
    ).then((items) => (Array.isArray(items) ? items.map(readHistoryItem) : []));
  },

  /** Step 4. Rejects on a backend without records (callers hide the block). */
  exerciseRecords(exerciseId: string) {
    return request<unknown>(recordsPath(exerciseId)).then(readRecords);
  },

  /**
   * Step 6. Rejects on a backend without progress; null when the answer is not a progress
   * response (callers hide the chart). `fallbackKind` names the metric for a bare array.
   */
  exerciseProgress(exerciseId: string, range: ProgressRange, fallbackKind: RecordKind | null = null) {
    return request<unknown>(progressPath(exerciseId, range)).then((r) => readProgress(r, fallbackKind));
  },

  listCatalogExercises(params?: { q?: string; muscleGroup?: string }) {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.muscleGroup) q.set("muscleGroup", params.muscleGroup);
    const s = q.toString();
    return request<CatalogExercise[]>(
      `/api/catalog/exercises${s ? `?${s}` : ""}`,
    ).then((list) => list.map(readCatalogExercise));
  },

  createCatalogExercise(body: CatalogWrite & { name: string }) {
    return writing(
      request<CatalogExercise>("/api/catalog/exercises", {
        method: "POST",
        body: JSON.stringify(body),
      }).then(readCatalogExercise),
      "catalog",
    );
  },

  /** Get-or-create catalog exercise by name (for in-workout creation). */
  ensureCatalogExercise(body: CatalogWrite & { name: string }) {
    return writing(
      request<CatalogExercise & { created: boolean }>(
        "/api/catalog/exercises/ensure",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ).then((r) => ({ ...readCatalogExercise(r), created: r.created })),
      "catalog",
    );
  },

  updateCatalogExercise(id: string, body: CatalogWrite & { name?: string }) {
    return writing(
      request<CatalogExercise>(`/api/catalog/exercises/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }).then(readCatalogExercise),
      ...CATALOG_EDIT_SCOPE,
    );
  },

  deleteCatalogExercise(id: string) {
    return writing(
      request(`/api/catalog/exercises/${id}`, { method: "DELETE" }),
      ...CATALOG_EDIT_SCOPE,
    );
  },

  listTemplates() {
    return request<WorkoutTemplate[]>("/api/templates").then((list) =>
      list.map(readTemplate),
    );
  },

  createTemplate(body: unknown) {
    return writing(
      request<WorkoutTemplate>("/api/templates", {
        method: "POST",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readTemplate),
      ...TEMPLATE_SCOPE,
    );
  },

  updateTemplate(id: string, body: unknown) {
    return writing(
      request<WorkoutTemplate>(`/api/templates/${id}`, {
        method: "PUT",
        body: JSON.stringify(withWireExercises(body)),
      }).then(readTemplate),
      ...TEMPLATE_SCOPE,
    );
  },

  deleteTemplate(id: string) {
    return writing(
      request(`/api/templates/${id}`, { method: "DELETE" }),
      ...TEMPLATE_SCOPE,
    );
  },

  getWeeklyGoal() {
    return request<WeeklyGoalProgress | null>("/api/goals/weekly");
  },

  setWeeklyGoal(changes: { targetCount?: number | null; weeklyGoalMinutes?: number | null }) {
    return writing(
      request<WeeklyGoalProgress>("/api/goals/weekly", {
        method: "PUT",
        body: JSON.stringify(writeWeeklyGoal(changes)),
      }),
      "profile",
    );
  },

  getMonthStats(year?: number, month?: number) {
    const q = new URLSearchParams();
    if (year) q.set("year", String(year));
    if (month) q.set("month", String(month));
    const s = q.toString();
    return request<MonthStats>(`/api/stats/month${s ? `?${s}` : ""}`).then(readMonthStats);
  },

  listBodyWeight(params?: { from?: string; to?: string; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.limit) q.set("limit", String(params.limit));
    const s = q.toString();
    return request<BodyWeightLog[]>(
      `/api/body-weight${s ? `?${s}` : ""}`,
    );
  },

  bodyWeightSummary(period: "30d" | "90d" | "all" = "30d") {
    return request<BodyWeightSummary>(
      `/api/body-weight/summary?period=${period}`,
    );
  },

  upsertBodyWeight(body: {
    date?: string;
    weight: number;
    notes?: string | null;
  }) {
    return writing(
      request<BodyWeightLog>("/api/body-weight", {
        method: "POST",
        body: JSON.stringify(body),
      }),
      "weight",
    );
  },

  updateBodyWeight(
    id: string,
    body: { weight?: number; notes?: string | null; date?: string },
  ) {
    return writing(
      request<BodyWeightLog>(`/api/body-weight/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
      "weight",
    );
  },

  deleteBodyWeight(id: string) {
    return writing(
      request(`/api/body-weight/${id}`, { method: "DELETE" }),
      "weight",
    );
  },

  adminStats() {
    return request<PlatformStats>("/api/admin/stats");
  },

  adminListUsers(params?: {
    q?: string;
    page?: number;
    pageSize?: number;
    isAdmin?: boolean;
  }) {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.page) q.set("page", String(params.page));
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    if (params?.isAdmin === true) q.set("isAdmin", "true");
    if (params?.isAdmin === false) q.set("isAdmin", "false");
    const s = q.toString();
    return request<AdminUserListResponse>(
      `/api/admin/users${s ? `?${s}` : ""}`,
    );
  },

  adminGetUser(id: string) {
    return request<AdminUserDetail>(`/api/admin/users/${id}`);
  },

  adminSetUserAdmin(id: string, isAdmin: boolean) {
    return request<AdminUserRow>(`/api/admin/users/${id}/admin`, {
      method: "PATCH",
      body: JSON.stringify({ isAdmin }),
    });
  },
};
